import { createSelector } from '@reduxjs/toolkit'
import moment from 'moment'
import { IGrant,IMatchedGrant,ISolicitation } from '../../types/Grants'
import { convertPhase } from '../../utils'
import { RootState } from '../store'
import { normalizeTechGrants } from './utils'

const grantsColumnsToRemove = ['status', 'refId', 'like']

const getGrants = (state: RootState) => state.technology.grants.data
const getLoading = (state: RootState) => state.technology.grants.loading
const error = (state: RootState) => state.technology.grants.error
const getPaging = (state: RootState) => state.technology.grants.paging

const makeGrantsSummary = createSelector(
  getGrants,
  (grants) => {
    return normalizeTechGrants(grants)
  },
)

const makePipelineGrants = createSelector(makeGrantsSummary, (grants) => {
  return grants.reduce((acc: Record<string, { grants: typeof grants; totalAmount: number }>, grant) => {
    if (grant.status !== 'selected' || grant.outcome !== 'pending') {
      return acc
    }

    const { progressStatus = 'unknown', totalAwardAmount = 0 } = grant

    if (!acc[progressStatus]) {
      acc[progressStatus] = {
        grants: [],
        totalAmount: 0,
      }
    }

    acc[progressStatus].grants.push(grant)
    acc[progressStatus].totalAmount += totalAwardAmount

    return acc
  }, {})
})

const makePipelineHasData = createSelector(makePipelineGrants, (pipelineGrants) => {
  const stages = Object.keys(pipelineGrants)

  return stages.length > 0 && stages.some(stage => pipelineGrants[stage].grants.length > 0)
})

const makeMatchedGrants = createSelector(makeGrantsSummary, (grants) => {
  return grants.filter((g:any) => g.status === 'recommended')
})

const makeSubmittedGrants = createSelector(makeGrantsSummary, (grants) => {
  return grants.filter((g:any) => g.progressStatus === 'submitted')
})

const makeExportGrants = createSelector(makeGrantsSummary, (grants: any[]) => {
  if (!grants || !grants.length) return null

  const updatedGrants = grants.map((grant) => {
    const { progressStatus, topicId, ...rest } = grant
    return {
      Status: progressStatus,
      topic: topicId,
      ...rest,
    }
  })

  const columns = Object.keys(updatedGrants[0]).filter(
    (column) => !grantsColumnsToRemove.includes(column),
  )

  const rows: string[][] = updatedGrants.map((item: any) => {
    const { status, refId, like, ...filteredItem } = item

    return columns.map((c) => {
      const cellValue = (filteredItem as any)[c]
      if (cellValue === 'NA' || cellValue === null || cellValue === undefined) {
        return ''
      }

      if (cellValue && isNaN(Number(cellValue))) {
        return cellValue.replace(/\r|\n/g, '')
      }

      return cellValue
    })
  })

  return { rows, columns }
})

const makeGrantsIds = createSelector(
  getGrants,
  (grants) => {
    return grants.map((g: IMatchedGrant) => {
      const { grant = {} as IGrant } = g

      return grant._id
    })
  },
)

const makeTotalWonGrantsAmount = createSelector(
    makeGrantsSummary,
    (grantsSummary) => {
      if(!grantsSummary) return 0

      return grantsSummary
          .filter(grant => grant.outcome === 'win')
          .reduce((total, grant) => total + grant.totalAwardAmount, 0)
    }
)

export const TechGrantSelectors = {
  error,
  getPaging,
  getGrants,
  getLoading,
  makeGrantsIds,
  makeExportGrants,
  makeMatchedGrants,
  makeGrantsSummary,
  makePipelineGrants,
  makeSubmittedGrants,
  makePipelineHasData,
  makeTotalWonGrantsAmount
}
